import { useState, useEffect, useMemo } from "react";
import { X, Search, Users, Loader2 } from "lucide-react";
import { fetchClient } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { usePeopleSearch, type PersonResult } from "@/hooks/usePeopleSearch";

interface AddProjectMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  projectTenantId?: string;
  existingMembers?: Array<{
    id: string;
    email: string;
    display_name: string;
    first_name: string;
    last_name: string;
    tenant_id?: string;
  }>;
  onMembersChanged?: () => void;
}

export default function AddProjectMembersModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  existingMembers = [],
  onMembersChanged,
}: AddProjectMembersModalProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCloseBtn, setHoveredCloseBtn] = useState(false);
  const [hoveredDeleteId, setHoveredDeleteId] = useState<string | null>(null);
  const [hoveredResultId, setHoveredResultId] = useState<string | null>(null);
  const [hoveredDoneBtn, setHoveredDoneBtn] = useState(false);

  const excludeEmails = useMemo(() => existingMembers.map(m => m.email), [existingMembers]);
  const { results: searchResults, searching } = usePeopleSearch(
    isOpen ? searchQuery : '',
    excludeEmails,
  );

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setError(null);
    }
  }, [isOpen]);

  const handleAddMember = async (person: PersonResult) => {
    if (!person.email) return;
    setSaving(true);
    setError(null);
    try {
      const { error: postError } = await fetchClient.POST(
        "/api/v1/projects/{project_id}/members",
        {
          params: { path: { project_id: projectId } },
          body: {
            email: person.email,
            display_name: person.displayName,
            role: person.jobTitle || undefined,
          },
        },
      );

      if (postError) {
        setError("Failed to add member");
      } else {
        // Clear search and refresh
        setSearchQuery("");
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/v1/projects/{project_id}/members"],
        });
        if (onMembersChanged) onMembersChanged();
      }
    } catch {
      setError("Failed to add member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!confirm("Remove this member from the project?")) return;
    try {
      const { error: deleteError } = await fetchClient.DELETE(
        "/api/v1/projects/{project_id}/members/{member_id}",
        { params: { path: { project_id: projectId, member_id: memberId } } },
      );
      if (deleteError) {
        setError("Failed to remove member");
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/v1/projects/{project_id}/members"],
      });
      if (onMembersChanged) onMembersChanged();
    } catch {
      setError("Failed to remove member");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
      <div
        className="rounded-lg shadow-xl w-full max-w-lg h-[90vh] flex flex-col"
        style={{ backgroundColor: "var(--surface-alt)" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 flex-shrink-0"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
            <h2 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>Edit Members</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{
              backgroundColor: hoveredCloseBtn ? "var(--secondary)" : "transparent",
            }}
            onMouseEnter={() => setHoveredCloseBtn(true)}
            onMouseLeave={() => setHoveredCloseBtn(false)}
          >
            <X className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden p-4">
          <div className="mb-4 flex-shrink-0">
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Add members to <span className="font-medium" style={{ color: "var(--text-primary)" }}>{projectName}</span>
            </p>
          </div>

          {error && (
            <div
              className="mb-4 p-3 rounded-md flex-shrink-0"
              style={{
                backgroundColor: "color-mix(in srgb, var(--error) 10%, transparent)",
                border: "1px solid color-mix(in srgb, var(--error) 30%, transparent)",
              }}
            >
              <p className="text-sm" style={{ color: "var(--error)" }}>{error}</p>
            </div>
          )}

          {/* Existing Project Members */}
          {existingMembers.length > 0 && (
            <div className="mb-4 flex-shrink-0">
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Project Members ({existingMembers.length})
              </label>
              <div
                className="space-y-0 max-h-48 overflow-y-auto rounded-md"
                style={{
                  border: "1px solid var(--border)",
                  backgroundColor: "var(--surface-alt)",
                }}
              >
                {existingMembers.map((member, index) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3"
                      style={{
                        borderBottom: index < existingMembers.length - 1 ? "1px solid var(--border)" : "none",
                      }}
                    >
                      <div className="flex-1">
                        <div
                          className="font-medium text-sm"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {member.display_name || `${member.first_name} ${member.last_name}`.trim() || member.email}
                        </div>
                        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{member.email}</div>
                      </div>
                      <button
                        onClick={() => handleDeleteMember(member.id)}
                        className="ml-3 p-1.5 rounded transition-colors"
                        style={{
                          color: hoveredDeleteId === member.id ? "var(--error)" : "var(--muted-foreground)",
                          backgroundColor: hoveredDeleteId === member.id ? "color-mix(in srgb, var(--error) 10%, transparent)" : "transparent",
                        }}
                        onMouseEnter={() => setHoveredDeleteId(member.id)}
                        onMouseLeave={() => setHoveredDeleteId(null)}
                        title="Remove from project"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                ))}
              </div>
            </div>
          )}

          {/* Search People */}
          <div className="flex-1 flex flex-col min-h-0">
            <label
              className="block text-sm font-medium mb-2 flex-shrink-0"
              style={{ color: "var(--text-primary)" }}
            >
              Search People
            </label>

            {/* Search Input */}
            <div className="relative mb-3 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-10 pr-3 py-2 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                style={{
                  border: "1px solid var(--border)",
                  backgroundColor: "var(--surface-alt)",
                  color: "var(--text-primary)",
                }}
                autoFocus
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: "var(--muted-foreground)" }} />
              )}
            </div>

            {/* Results */}
            <div
              className="flex-1 overflow-y-auto rounded-md min-h-0"
              style={{
                border: "1px solid var(--border)",
                backgroundColor: "var(--surface-alt)",
              }}
            >
              {searchQuery.length < 2 ? (
                <div className="px-4 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
                  Type at least 2 characters to search
                </div>
              ) : searching ? (
                <div className="px-4 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
                  Searching...
                </div>
              ) : searchResults.length > 0 ? (
                <div>
                  {searchResults.map((person, index) => (
                    <button
                      key={person.id}
                      type="button"
                      onClick={() => handleAddMember(person)}
                      disabled={saving}
                      className="w-full text-left px-4 py-3 transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: hoveredResultId === person.id ? "var(--secondary)" : "transparent",
                        borderBottom: index < searchResults.length - 1 ? "1px solid var(--border)" : "none",
                      }}
                      onMouseEnter={() => setHoveredResultId(person.id)}
                      onMouseLeave={() => setHoveredResultId(null)}
                    >
                      <div className="font-medium text-sm" style={{ color: "var(--text-primary)" }}>
                        {person.displayName}
                      </div>
                      <div className="flex items-center gap-2 text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                        {person.email && <span>{person.email}</span>}
                        {person.jobTitle && (
                          <>
                            <span>--</span>
                            <span>{person.jobTitle}</span>
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
                  {searchQuery ? `No people found matching "${searchQuery}"` : "No results"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 p-4 flex-shrink-0"
          style={{
            borderTop: "1px solid var(--border)",
            backgroundColor: "var(--secondary)",
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md"
            style={{
              color: "var(--text-primary)",
              backgroundColor: hoveredDoneBtn ? "var(--secondary)" : "var(--surface-alt)",
              border: "1px solid var(--border)",
            }}
            onMouseEnter={() => setHoveredDoneBtn(true)}
            onMouseLeave={() => setHoveredDoneBtn(false)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
